import type {ReactNode} from 'react';

import {Button} from 'shells/button/button';
import type {AddButtonProps, EmptyStateProps} from 'shells/card/card-widget/card-widget';
import {colors} from 'shells/colors/colors';
import {CompanyRegularIcon} from 'shells/icon/svg/company-regular-icon';
import {HierarchyRegularIcon} from 'shells/icon/svg/hierarchy-regular-icon';
import {ParentCompanyIcon} from 'shells/icon/svg/parent-company-icon';
// @ts-expect-error - imported from .js/.jsx file
import {UpgradeArrowIcon} from 'shells/icon/svg/upgrade-arrow-icon';
import type {SelectMenuOption} from 'shells/select-menu/select-menu';

import type {AddingAccountRelationship} from './account-hierarchy-widget';
import GET_ACCOUNT_HIERARCHY_WIDGET from './graphql/queries/get-account-hierarchy-widget.graphql';

export const getAddAccountButtonProps = (
    setRelationshipToAdd: (isAddingAccount: AddingAccountRelationship) => void,
    hasParent: boolean,
    hasChild: boolean,
    isEmpty: boolean
): AddButtonProps | undefined => {
    if (!NutClientConfig.hasAccountHierarchy) {
        return undefined;
    }

    const selectMenuOptions: SelectMenuOption[] = [];
    if (isEmpty) {
        selectMenuOptions.push({
            label: 'Add a parent company',
            icon: <ParentCompanyIcon size={16} wrapWithDiv={false} />,
            handleSelect: () => {
                setRelationshipToAdd('parent');
            },
        });
    }
    if ((!hasParent && hasChild) || isEmpty) {
        selectMenuOptions.push({
            label: 'Add a child company',
            icon: <CompanyRegularIcon size={16} wrapWithDiv={false} />,
            handleSelect: () => {
                setRelationshipToAdd('child');
            },
        });
    }

    const addButtonProps: AddButtonProps = {
        variant: 'multiple',
        selectMenuOptions: selectMenuOptions,
    };

    return selectMenuOptions.length ? addButtonProps : undefined;
};

export const getRefetchQueries = (accountId: string): any[] => {
    return [{query: GET_ACCOUNT_HIERARCHY_WIDGET, variables: {id: accountId}}];
};

export const getEmptyStateProps = (
    isEmpty: boolean,
    relationshipToAdd: boolean,
    isRefetching: boolean
): EmptyStateProps => {
    if (!NutClientConfig.hasAccountHierarchy) {
        return {
            isEmpty: true,
            text: 'Upgrade your plan to access company hierarchy',
            icon: HierarchyRegularIcon,
            iconSize: 22,
        };
    }

    return {
        isEmpty: isEmpty && !relationshipToAdd && !isRefetching,
        text: 'No parent or child companies added',
        icon: HierarchyRegularIcon,
        iconSize: 22,
    };
};

export const getSecondaryUpgradeTitle = (onOpenUpgradeModal: () => void): ReactNode | undefined => {
    if (NutClientConfig.hasAccountHierarchy) {
        return undefined;
    }

    return (
        <Button variant='text-primary' noPadding={true} onClick={onOpenUpgradeModal}>
            <div className='flex align-center gap-4'>
                Upgrade
                <UpgradeArrowIcon size={12} wrapWithDiv={false} color={colors.blue} />
            </div>
        </Button>
    );
};
